/**
 * Auth strategy abstraction.
 *
 * We have two distinct backends to support:
 *
 *   1. java_backend (current) — Bearer-token authentication. The token is
 *      returned in the JSON body of POST /api/obd/login and the frontend
 *      attaches it to subsequent requests as `Authorization: Bearer <token>`.
 *
 *   2. obd3-api (future) — httpOnly session cookies set by the server.
 *      The frontend never sees the token. `withCredentials: true` makes
 *      axios send the cookie automatically. Logout is a server endpoint.
 *
 * Feature code only talks to this strategy interface; swapping the
 * concrete implementation is a one-file change when obd3-api is ready.
 */

export interface UserPayload {
  userId: string;
  role: string;
  /** Optional display fields, populated when login response includes them. */
  name?: string;
  username?: string;
}

export interface LoginResult {
  user: UserPayload;
  /** Bearer strategies only — null on cookie strategies. */
  token: string | null;
}

export interface AuthStrategy {
  /**
   * Authoritative token retrieval — Bearer strategies return the stored
   * token; cookie strategies always return null (the browser sends the
   * cookie automatically).
   */
  getAccessToken: () => string | null;
  /**
   * Whether the user is currently authenticated as far as the client can
   * tell. For Bearer strategies this is "token + user are present".
   * For cookie strategies it's "user payload is present" (because the
   * token is invisible to JS).
   */
  isAuthenticated: () => boolean;
  getUser: () => UserPayload | null;
  /** Persist the login result. Called after a successful /login. */
  setSession: (result: LoginResult) => void;
  /** Wipe local auth state. Called on logout AND on 401. */
  clearSession: () => void;
  /**
   * Whether the axios client should send `withCredentials: true`. Required
   * for httpOnly-cookie strategies; OFF for Bearer because the backend
   * may respond with `Access-Control-Allow-Origin: *` which browsers
   * refuse with credentials mode on.
   */
  needsCredentials: boolean;
}

/* --------------------------------------------------------------- *
 * Bearer-token strategy backed by sessionStorage + in-memory user. *
 * --------------------------------------------------------------- */

const TOKEN_KEY = 'obd3:auth:token';
const USER_KEY = 'obd3:auth:user';

const LEGACY_SESSION_KEYS = [
  'user',
  'access_token',
  'userStack',
  'user_backup',
  'activeLoginType',
  'loginTimestamp',
  'idleTimestamp',
  'activeBreak',
];
const USER_SCOPED_CACHE_KEYS = ['persist:root'];
// 'obd3:filters:' = saved UI filters (lib/filters/persisted-state.ts): reset on logout / switch / switch back.
const USER_SCOPED_CACHE_PREFIXES = ['portal_report_cache_v1:', 'obd3:filters:'];

export function clearUserScopedCaches(): void {
  try {
    [sessionStorage, localStorage].forEach((store) => {
      USER_SCOPED_CACHE_KEYS.forEach((k) => store.removeItem(k));

      const doomed: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (key && USER_SCOPED_CACHE_PREFIXES.some((p) => key.startsWith(p))) {
          doomed.push(key);
        }
      }
      doomed.forEach((k) => store.removeItem(k));
    });
  } catch {
    /* storage unavailable — no-op */
  }
}

export function clearLegacySessionKeys(): void {
  try {
    LEGACY_SESSION_KEYS.forEach((k) => {
      sessionStorage.removeItem(k);
      localStorage.removeItem(k);
    });
  } catch {
    /* storage unavailable — no-op */
  }
  clearUserScopedCaches();
}

function hasLegacyStack(): boolean {
  try {
    const raw = sessionStorage.getItem('userStack');
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

function readUser(): UserPayload | null {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as UserPayload) : null;
  } catch {
    return null;
  }
}

function readToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export const bearerSessionStorageStrategy: AuthStrategy = {
  needsCredentials: false,
  getAccessToken: () => readToken(),
  getUser: () => readUser(),
  isAuthenticated: () => Boolean(readToken() && readUser()),
  setSession: (result) => {
    try {
      if (result.token) sessionStorage.setItem(TOKEN_KEY, result.token);
      sessionStorage.setItem(USER_KEY, JSON.stringify(result.user));
    } catch {
      /* sessionStorage unavailable (private mode) — silently degrade. */
    }
  },
  clearSession: () => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
      /*
       * Wipe auth-derived caches but PRESERVE user preferences (theme,
       * table column choices). The legacy app called localStorage.clear()
       * which was too aggressive.
       */
      const keysToClear: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key) continue;
        if (key === 'obd3:permissions' || key.startsWith('obd3:auth:')) {
          keysToClear.push(key);
        }
      }
      keysToClear.forEach((k) => localStorage.removeItem(k));
      if (!hasLegacyStack()) clearLegacySessionKeys();
    } catch {
      /* noop */
    }
  },
};

/* --------------------------------------------------------------- *
 * Future strategy: httpOnly cookies via obd3-api.                   *
 * Implemented as a placeholder so the swap is a one-file edit.     *
 * --------------------------------------------------------------- */

/*
 * Coexistence model — obd3-api MUST support both Bearer (for existing
 * API consumers) and httpOnly cookies (for browser clients) at the
 * SAME endpoints:
 *
 *   Auth middleware on every protected route:
 *     1. Try Cookie: obd3_session  → verify; if ok, set req.user, continue.
 *     2. Else if Authorization: Bearer …  → verify token, set req.user, continue.
 *     3. Else → 401.
 *
 *   POST /api/obd/login response branches on the caller:
 *     - Browser (Origin header matches obd3-gui hosts):
 *         res.cookie('obd3_session', …, { httpOnly, secure, sameSite: 'lax' })
 *         body: { userId, role, name }   // no token
 *     - API client (no matching Origin / explicit `X-Auth-Mode: bearer`):
 *         body: { userId, token, role }  // legacy Bearer flow, unchanged
 *
 *   POST /api/obd/logout clears the session cookie server-side.
 *   POST /api/obd/refresh exchanges obd3_refresh for a fresh obd3_session.
 *
 * Browser frontend (this file) then uses `httpOnlyCookieStrategy` below;
 * direct-to-API users continue to use Bearer exactly as today — never
 * affected. Both methods land on the same req.user.
 */

// export const httpOnlyCookieStrategy: AuthStrategy = {
//   getAccessToken: () => null,             // backend reads cookie automatically
//   getUser: () => /* from a cached user query, e.g. React Query "me" */,
//   isAuthenticated: () => Boolean(getUser()),
//   setSession: ({ user }) => { /* server already set the cookie; cache user */ },
//   clearSession: () => { /* POST /api/obd/logout then drop cached user */ },
// };

/**
 * Active strategy. To migrate the browser to obd3-api's cookie auth,
 * swap this export for `httpOnlyCookieStrategy`. Bearer API clients are
 * unaffected — see coexistence notes above.
 */
export const authStrategy: AuthStrategy = bearerSessionStorageStrategy;
